import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { authAxios } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

export default function AdminNotes() {
  const { currentSeason, API } = useContext(AppContext);
  const [showNotes, setShowNotes] = useState(false);
  const [notesTitle, setNotesTitle] = useState('Gamekeeper\'s Notes');
  const [notesContent, setNotesContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentSeason) {
      loadData();
    }
  }, [currentSeason]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await authAxios.get(`${API}/seasons/${currentSeason.id}`);
      setShowNotes(response.data.show_notes || false);
      setNotesTitle(response.data.notes_title || 'Gamekeeper\'s Notes');
      setNotesContent(response.data.notes_content || '');
    } catch (error) {
      console.error('Failed to load season data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveNotes = async () => {
    try {
      setSaving(true);
      await authAxios.patch(
        `${API}/seasons/${currentSeason.id}`,
        {
          show_notes: showNotes,
          notes_title: notesTitle,
          notes_content: notesContent
        },
        { withCredentials: true }
      );
      toast.success('Notes updated successfully!');
    } catch (error) {
      console.error('Failed to save notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto container-mobile">
        <div className="spacing-section">
          <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Publish a Message
          </h1>
          <p className="text-responsive text-slate-600">Manage announcements shown on players' home screen</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Preview Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-900">Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-blue-700 mb-4">
                  This is how the notes will appear on players' home screens:
                </p>
                {showNotes ? (
                  <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                    <CardHeader>
                      <CardTitle className="text-purple-900 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        {notesTitle || 'Gamekeeper\'s Notes'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {notesContent ? (
                        <div className="text-slate-700 whitespace-pre-wrap">{notesContent}</div>
                      ) : (
                        <p className="text-slate-400 italic">No content yet...</p>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-300 rounded-lg">
                    Notes are currently hidden from players
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Settings Card */}
            <Card>
              <CardHeader>
                <CardTitle>Notes Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Toggle Visibility */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showNotes}
                      onChange={(e) => setShowNotes(e.target.checked)}
                      className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500"
                    />
                    <div>
                      <div className="font-medium text-slate-800">Show notes on Home screen</div>
                      <p className="text-sm text-slate-600">When enabled, players will see this section on their dashboard</p>
                    </div>
                  </label>
                </div>

                {/* Title */}
                <div>
                  <Label>Notes Title</Label>
                  <Input
                    value={notesTitle}
                    onChange={(e) => setNotesTitle(e.target.value)}
                    placeholder="Gamekeeper's Notes"
                    className="mt-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    This appears as the heading. Default: "Gamekeeper's Notes"
                  </p>
                </div>

                {/* Content */}
                <div>
                  <Label>Notes Content</Label>
                  <Textarea
                    value={notesContent}
                    onChange={(e) => setNotesContent(e.target.value)}
                    placeholder="Enter tasks, announcements, or instructions for players...

Examples:
• Contestant Draft: Friday, March 15th at 8 PM EST
• Weekly Bonus Question due by Sunday night
• Don't forget to check your roster before Episode 5!"
                    rows={12}
                    className="mt-2 font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Supports line breaks. Use bullet points (•) for lists.
                  </p>
                </div>

                {/* Save Button */}
                <div className="flex gap-3">
                  <Button onClick={saveNotes} disabled={saving} className="flex-1">
                    {saving ? 'Saving...' : 'Save Notes'}
                  </Button>
                  <Button variant="outline" onClick={loadData} disabled={saving}>
                    Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
